import React, { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { fmtInt, fmtEUR } from "@/lib/api";

// The slider moves on an even "position" scale where each pair of consecutive
// breakpoints (100→200→500→1.000→5.000) spans one equal segment. This keeps
// the thumb aligned with the labels underneath — a plain linear 100–5000
// slider would show ~1300 τεμ. at the "200" mark.
const SEG = 1000; // slider units per breakpoint segment

const posToQty = (bps, pos) => {
  const i = Math.max(0, Math.min(bps.length - 2, Math.floor(pos / SEG)));
  const t = pos / SEG - i;
  const raw = bps[i] + (bps[i + 1] - bps[i]) * t;
  return Math.round(raw / 10) * 10;
};

const qtyToPos = (bps, qty) => {
  const q = Math.max(bps[0], Math.min(bps[bps.length - 1], qty));
  let i = bps.length - 2;
  for (let k = 0; k < bps.length - 1; k += 1) {
    if (q <= bps[k + 1]) {
      i = k;
      break;
    }
  }
  return ((q - bps[i]) / (bps[i + 1] - bps[i]) + i) * SEG;
};

export const QuantityControl = ({ pricing, value, onChange, screen, priceActive }) => {
  // Local text state so the user can freely edit / clear the field.
  // The committed (clamped) value is applied on blur or Enter.
  const [draft, setDraft] = useState(String(value));

  // Keep the draft in sync when the value changes from outside (e.g. slider).
  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const parsed = parseInt(draft, 10);
    if (Number.isNaN(parsed)) {
      setDraft(String(value));
      return;
    }
    onChange(parsed);
  };

  // Pro tip: shown only for the Video Brochure when the quantity is below the
  // recommended minimum of 200. Numbers are computed from the selected screen.
  const tiers = pricing.matrix?.[screen];
  const p100 = tiers?.["100"];
  const p200 = tiers?.["200"];
  const reductionPct =
    p100 && p200 ? Math.round((1 - p200 / p100) * 100) : null;
  const showProTip = priceActive && value < 200 && reductionPct != null;

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between">
        <label className="text-sm font-semibold text-black/70">Ποσότητα (τεμάχια)</label>
        {value <= 200 && (
          <span className="rounded-full bg-brand-50/60 px-3 py-1 text-xs font-medium text-brand-700">
            ★ 200+ συνιστάται
          </span>
        )}
      </div>
      <div className="mt-4 flex items-center gap-4">
        <div className="flex-1">
          <Slider
            data-testid="calculator-quantity-slider"
            value={[qtyToPos(pricing.breakpoints, value)]}
            min={0}
            max={(pricing.breakpoints.length - 1) * SEG}
            step={10}
            onValueChange={(v) => onChange(posToQty(pricing.breakpoints, v[0]))}
          />
          <div className="relative mt-2 h-4 text-[11px] text-black/40">
            {pricing.breakpoints.map((b, i) => (
              <span
                key={b}
                className="absolute top-0"
                style={{
                  left: `${(i / (pricing.breakpoints.length - 1)) * 100}%`,
                  transform:
                    i === 0
                      ? "none"
                      : i === pricing.breakpoints.length - 1
                        ? "translateX(-100%)"
                        : "translateX(-50%)",
                }}
              >
                {fmtInt(b)}
              </span>
            ))}
          </div>
        </div>
        <Input
          type="number"
          data-testid="calculator-quantity-input"
          value={draft}
          min={pricing.min_qty}
          max={pricing.max_qty}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            }
          }}
          className="w-24 text-center font-display font-semibold"
        />
      </div>

      {showProTip && (
        <div
          data-testid="calculator-protip"
          className="mt-5 rounded-2xl border border-brand-500/30 bg-brand-50/50 p-5"
        >
          <div className="flex items-center gap-2 font-display text-sm font-bold text-brand-700">
            ⚠️ Pro Tip
          </div>
          <p className="mt-2 text-sm leading-relaxed text-ink/75">
            Αυξάνοντας σε <strong>200 τεμάχια</strong> μειώνεις το κόστος ανά
            τεμάχιο κατά σχεδόν <strong>{reductionPct}%</strong> (στα{" "}
            <strong>{fmtEUR(p200)}/τεμ.</strong>). Είναι το προτεινόμενο ελάχιστο
            για το καλύτερο ROI.
          </p>
          <button
            type="button"
            data-testid="calculator-protip-switch"
            onClick={() => onChange(200)}
            className="group mt-4 inline-flex items-center gap-2 rounded-full bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-brand-600"
          >
            Άλλαξε σε 200 τεμάχια
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      )}
    </div>
  );
};
